import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { waLink } from "@/lib/contacts";
import { toast } from "sonner";
import {
  ArrowLeft, House, CheckCircle, MagnifyingGlass, WhatsappLogo,
} from "@phosphor-icons/react";
import QLogo from "@/components/QLogo";
import WizardFooter from "@/components/WizardFooter";

// 7 input steps. Step 0 = welcome (not counted in progress), Step 8 = success.
const INPUT_STEPS = 7;

// ---------- Option catalogs ----------
const BUDGET_BUCKETS = [
  { v: "3-5L",  min: 300000,  max: 500000,   l: "₹3 – 5 Lakhs" },
  { v: "5-8L",  min: 500000,  max: 800000,   l: "₹5 – 8 Lakhs" },
  { v: "8-10L", min: 800000,  max: 1000000,  l: "₹8 – 10 Lakhs" },
  { v: "10-15L",min: 1000000, max: 1500000,  l: "₹10 – 15 Lakhs" },
  { v: "15-20L",min: 1500000, max: 2000000,  l: "₹15 – 20 Lakhs" },
  { v: "20-30L",min: 2000000, max: 3000000,  l: "₹20 – 30 Lakhs" },
  { v: "30L+",  min: 3000000, max: 10000000, l: "₹30 Lakhs+" },
  { v: "custom",min: null,    max: null,     l: "Custom Budget" },
];

const BRANDS = [
  "Maruti Suzuki", "Hyundai", "Honda", "Toyota", "Mahindra", "Tata",
  "Kia", "MG", "Skoda", "Volkswagen", "Renault", "Nissan",
  "Jeep", "BMW", "Mercedes-Benz", "Audi", "Volvo", "Lexus",
  "Mini", "Land Rover", "BYD", "Citroen", "Force Motors", "Isuzu",
];
const BODY_TYPES = [
  "Hatchback", "Sedan", "SUV", "Compact SUV", "MUV",
  "Luxury Sedan", "Luxury SUV", "Pickup", "Convertible", "Coupe",
];
const FUELS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const TRANS = ["Manual", "Automatic"];
const OWNERSHIP_OPTIONS = [
  { v: "any", l: "No Preference" },
  { v: "first", l: "1st Owner" },
  { v: "second", l: "Up to 2 Owners" },
];

const YEAR_BUCKETS = [
  { v: 2016, l: "2016+" },
  { v: 2018, l: "2018+" },
  { v: 2020, l: "2020+" },
  { v: 2022, l: "2022+" },
  { v: 2024, l: "2024+" },
  { v: null, l: "No Preference" },
];

const KM_BUCKETS = [
  { v: 20000,  l: "Under 20,000 km" },
  { v: 40000,  l: "Under 40,000 km" },
  { v: 60000,  l: "Under 60,000 km" },
  { v: 80000,  l: "Under 80,000 km" },
  { v: 100000, l: "Under 1,00,000 km" },
  { v: null,   l: "No Preference" },
];

const INTENT_OPTIONS = [
  { v: "immediate",  l: "Immediate" },
  { v: "this_week",  l: "This Week" },
  { v: "this_month", l: "This Month" },
  { v: "exploring",  l: "Just Exploring" },
];
const YES_NO = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const BEST_TIME_CHIPS = [
  { v: "morning",   l: "Morning" },
  { v: "afternoon", l: "Afternoon" },
  { v: "evening",   l: "Evening" },
  { v: "anytime",   l: "Anytime" },
];

// ---------- Form defaults ----------
const DEFAULT_FORM = {
  budget_bucket: "",
  budget_min: 0, budget_max: 0,
  // chip selections (empty array = "No Preference" for multi-selects)
  brands: [], body_types: [], fuel_types: [], transmissions: [],
  preferred_model: "",
  ownership: "any",
  year_min: null,   // null = no preference
  max_km: null,     // null = no preference
  intent: "this_month",
  finance_required: false, exchange: false, color: "",
  // contact
  full_name: "", contact_phone: "", whatsapp_preferred: true, best_time: "anytime",
};

export default function Buy() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);          // 0 welcome → 7 input → 8 success
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [brandQuery, setBrandQuery] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) =>
    setForm((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const next = () => setStep((s) => Math.min(INPUT_STEPS + 1, s + 1));
  const back = () => (step > 0 ? setStep((s) => s - 1) : nav("/"));

  const visibleStep = Math.min(Math.max(step, 1), INPUT_STEPS); // for progress UI
  const progressPct = (visibleStep / INPUT_STEPS) * 100;

  // ---------- Budget chip handler ----------
  const pickBudget = (b) => {
    if (b.v === "custom") {
      update("budget_bucket", "custom");
      if (!form.budget_min && !form.budget_max) {
        setForm((p) => ({ ...p, budget_bucket: "custom", budget_min: 500000, budget_max: 1000000 }));
      }
    } else {
      setForm((p) => ({ ...p, budget_bucket: b.v, budget_min: b.min, budget_max: b.max }));
    }
  };

  const filteredBrands = useMemo(() => {
    const q = brandQuery.trim().toLowerCase();
    if (!q) return BRANDS;
    return BRANDS.filter((b) => b.toLowerCase().includes(q));
  }, [brandQuery]);

  // ---------- Validation ----------
  const canProceedFrom = (s) => {
    if (s === 1) {
      if (!form.budget_bucket) return false;
      if (form.budget_bucket === "custom" && (!form.budget_min || !form.budget_max || form.budget_max < form.budget_min)) return false;
      return true;
    }
    return true; // every other step accepts "No Preference"
  };

  // ---------- Submit ----------
  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("Please enter your name"); return; }
    if (form.contact_phone.replace(/\D/g, "").length !== 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setSubmitting(true);
    try {
      const payload = {
        budget_min: form.budget_min || 0,
        budget_max: form.budget_max || 0,
        brands: form.brands,
        body_types: form.body_types,
        models: [],
        preferred_model: form.preferred_model || null,
        fuel_types: form.fuel_types,
        transmissions: form.transmissions,
        ownership: form.ownership || "any",
        year_min: form.year_min || null,
        year_max: null,
        max_km: form.max_km || null,
        intent: form.intent || "exploring",
        finance_required: !!form.finance_required,
        exchange: !!form.exchange,
        color: form.color || null,
        full_name: form.full_name.trim(),
        contact_phone: `+91${form.contact_phone.replace(/\D/g, "")}`,
        whatsapp_preferred: !!form.whatsapp_preferred,
        best_time: form.best_time || "anytime",
      };
      await api.post("/requirements", payload);
      // GA4 / Google Ads conversion event — fires once per successful submit
      try { window.gtag && window.gtag("event", "buyer_requirement_submitted", { intent: form.intent }); } catch (_) { /* gtag blocked */ }
      setStep(INPUT_STEPS + 1);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      if (e?.response?.status === 429) {
        toast.error("Too many submissions from this network. Please try again in an hour.");
      } else {
        toast.error(formatApiErrorDetail(detail) || "Submission failed");
      }
    } finally { setSubmitting(false); }
  };

  // ---------- Render ----------
  return (
    <div className="min-h-screen pb-32" data-testid="buy-flow">
      <header className="px-6 pb-2 qd-pt-safe-header-lg"><QLogo size={32} /></header>

      <div className="px-6 pt-3 max-w-2xl mx-auto">
        {/* Top bar — back, progress, home */}
        {step > 0 && step <= INPUT_STEPS && (
          <div className="flex items-center justify-between mb-7" data-testid="buy-progress-bar">
            <button data-testid="buy-back-btn" onClick={back} className="text-white/60 hover:text-white flex items-center gap-1.5 text-xs">
              <ArrowLeft size={14} /> Back
            </button>
            <div className="flex-1 mx-4">
              <div className="h-[2px] bg-[#2A2A2A] relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-[#FF1E1E]" style={{ width: `${progressPct}%`, transition: "width 0.4s ease" }} />
              </div>
              <div className="text-[10px] font-mono-data tracking-widest text-white/50 mt-1.5 uppercase">
                Step {visibleStep} of {INPUT_STEPS}
              </div>
            </div>
            <button onClick={() => nav("/")} className="text-white/60 hover:text-white" data-testid="buy-home-btn"><House size={16} /></button>
          </div>
        )}

        {/* STEP 0 — Welcome */}
        {step === 0 && (
          <section data-testid="buy-step-welcome">
            <h1 className="qd-h1 text-3xl sm:text-4xl text-white mb-2" style={{ letterSpacing: "-0.02em" }}>Looking for a Car?</h1>
            <p className="text-white/55 text-sm mb-7 max-w-md">Our sourcing team will find the right car for you. No login required — takes under 2 minutes.</p>

            <ul className="mb-8 divide-y divide-white/8" data-testid="buy-trust-list">
              {[
                ["Verified Vehicles", "Every car inspected and document-verified before listing."],
                ["Trusted Dealer Network", "Sourced through Q Drives direct dealership partnerships."],
                ["Inspection Assistance", "Independent physical inspection arranged on request."],
              ].map(([t, d]) => (
                <li key={t} className="py-3.5 flex items-start gap-3">
                  <CheckCircle size={18} weight="fill" className="text-[#FF1E1E] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-white leading-tight">{t}</div>
                    <div className="text-[12px] text-white/55 mt-1 leading-snug">{d}</div>
                  </div>
                </li>
              ))}
            </ul>

            <WizardFooter>
              <button data-testid="buy-start" className="btn-primary w-full" onClick={next}>Start Your Search</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 1 — Budget */}
        {step === 1 && (
          <section data-testid="buy-step-budget">
            <h2 className="qd-h1 text-2xl text-white mb-2">What&apos;s your budget?</h2>
            <p className="text-white/55 text-sm mb-6">Tap a range or choose Custom.</p>

            <div className="grid grid-cols-2 gap-2.5 mb-6">
              {BUDGET_BUCKETS.map((b) => (
                <button
                  key={b.v}
                  data-testid={`budget-${b.v}`}
                  onClick={() => pickBudget(b)}
                  className={`qd-chip-card ${form.budget_bucket === b.v ? "active" : ""}`}
                >
                  {b.l}
                </button>
              ))}
            </div>

            {form.budget_bucket === "custom" && (
              <div className="mb-6 grid grid-cols-2 gap-3" data-testid="custom-budget">
                <label className="block">
                  <span className="qd-label">Min (₹)</span>
                  <input
                    data-testid="custom-budget-min"
                    type="number" inputMode="numeric"
                    className="qd-input-block"
                    value={form.budget_min || ""}
                    onChange={(e) => update("budget_min", Number(e.target.value) || 0)}
                    placeholder="e.g. 500000"
                  />
                </label>
                <label className="block">
                  <span className="qd-label">Max (₹)</span>
                  <input
                    data-testid="custom-budget-max"
                    type="number" inputMode="numeric"
                    className="qd-input-block"
                    value={form.budget_max || ""}
                    onChange={(e) => update("budget_max", Number(e.target.value) || 0)}
                    placeholder="e.g. 1500000"
                  />
                </label>
              </div>
            )}

            <WizardFooter>
              <button
                data-testid="buy-continue-budget"
                className="btn-primary w-full disabled:opacity-40"
                onClick={next}
                disabled={!canProceedFrom(1)}
              >Continue</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 2 — Brand + Vehicle Type */}
        {step === 2 && (
          <section data-testid="buy-step-brand">
            <h2 className="qd-h1 text-2xl text-white mb-2">Brand &amp; Vehicle Type</h2>
            <p className="text-white/55 text-sm mb-5">Select all that apply &mdash; or leave blank for No Preference.</p>

            {/* Sticky brand search */}
            <div className="sticky top-0 z-10 -mx-6 px-6 pt-1 pb-3 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/5 mb-4">
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="bold" />
                <input
                  data-testid="brand-search"
                  className="qd-input-block pl-9"
                  placeholder="Search brand…"
                  value={brandQuery}
                  onChange={(e) => setBrandQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="qd-label">Brand</label>
              <div className="flex flex-wrap gap-2 mb-2">
                <button
                  data-testid="brand-no-preference"
                  className={`qd-chip ${form.brands.length === 0 ? "active" : ""}`}
                  onClick={() => update("brands", [])}
                >No Preference</button>
                {filteredBrands.map((b) => (
                  <button
                    key={b}
                    data-testid={`brand-${b}`}
                    className={`qd-chip ${form.brands.includes(b) ? "active" : ""}`}
                    onClick={() => toggleArr("brands", b)}
                  >{b}</button>
                ))}
              </div>
              {filteredBrands.length === 0 && (
                <div className="text-xs text-white/40 mt-2">No matches. Clear the search to see all brands.</div>
              )}
            </div>

            <div className="mb-2">
              <label className="qd-label">Vehicle Type</label>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="body-no-preference"
                  className={`qd-chip ${form.body_types.length === 0 ? "active" : ""}`}
                  onClick={() => update("body_types", [])}
                >No Preference</button>
                {BODY_TYPES.map((b) => (
                  <button
                    key={b}
                    data-testid={`body-${b}`}
                    className={`qd-chip ${form.body_types.includes(b) ? "active" : ""}`}
                    onClick={() => toggleArr("body_types", b)}
                  >{b}</button>
                ))}
              </div>
            </div>

            <WizardFooter>
              <button data-testid="buy-continue-brand" className="btn-primary w-full" onClick={next}>Continue</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 3 — Year + Max KM */}
        {step === 3 && (
          <section data-testid="buy-step-year-km">
            <h2 className="qd-h1 text-2xl text-white mb-2">Year &amp; Kilometres</h2>
            <p className="text-white/55 text-sm mb-6">Pick what matters or leave on No Preference.</p>

            <FieldRow label="Manufacturing Year">
              <ChipGrid testid="year" cols={3}
                value={form.year_min}
                options={YEAR_BUCKETS}
                onPick={(v) => update("year_min", v)}
              />
            </FieldRow>

            <FieldRow label="Maximum Kilometres">
              <ChipGrid testid="km" cols={2}
                value={form.max_km}
                options={KM_BUCKETS}
                onPick={(v) => update("max_km", v)}
              />
            </FieldRow>

            <WizardFooter>
              <button data-testid="buy-continue-year-km" className="btn-primary w-full" onClick={next}>Continue</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 4 — Fuel + Transmission + Ownership */}
        {step === 4 && (
          <section data-testid="buy-step-mech">
            <h2 className="qd-h1 text-2xl text-white mb-2">Powertrain &amp; Ownership</h2>
            <p className="text-white/55 text-sm mb-6">Pick what matters or leave blank.</p>

            <FieldRow label="Fuel Type">
              <ChipMulti testid="fuel" value={form.fuel_types} options={FUELS}
                onToggle={(v) => toggleArr("fuel_types", v)}
                onClear={() => update("fuel_types", [])} />
            </FieldRow>
            <FieldRow label="Transmission">
              <ChipMulti testid="trans" value={form.transmissions} options={TRANS}
                onToggle={(v) => toggleArr("transmissions", v)}
                onClear={() => update("transmissions", [])} />
            </FieldRow>
            <FieldRow label="Ownership">
              <ChipSingle testid="own" value={form.ownership} options={OWNERSHIP_OPTIONS}
                onPick={(v) => update("ownership", v)} />
            </FieldRow>
            <FieldRow label="Preferred Model (Optional)">
              <input
                data-testid="preferred-model"
                className="qd-input-block"
                placeholder="e.g. Creta SX(O), City ZX CVT, Fortuner 4x4"
                value={form.preferred_model}
                onChange={(e) => update("preferred_model", e.target.value)}
              />
            </FieldRow>

            <WizardFooter>
              <button data-testid="buy-continue-mech" className="btn-primary w-full" onClick={next}>Continue</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 5 — Purchase Intent + Finance + Exchange + Color */}
        {step === 5 && (
          <section data-testid="buy-step-intent">
            <h2 className="qd-h1 text-2xl text-white mb-2">Purchase Plan</h2>
            <p className="text-white/55 text-sm mb-6">Helps us prioritize the right cars for you.</p>

            <FieldRow label="When do you plan to buy?">
              <ChipSingle testid="intent" value={form.intent} options={INTENT_OPTIONS}
                onPick={(v) => update("intent", v)} />
            </FieldRow>
            <FieldRow label="Need Finance?">
              <ChipSingle testid="fin" value={form.finance_required ? "yes" : "no"} options={YES_NO}
                onPick={(v) => update("finance_required", v === "yes")} />
            </FieldRow>
            <FieldRow label="Exchanging an existing car?">
              <ChipSingle testid="exch" value={form.exchange ? "yes" : "no"} options={YES_NO}
                onPick={(v) => update("exchange", v === "yes")} />
            </FieldRow>
            <FieldRow label="Colour Preference (Optional)">
              <input
                data-testid="color-input"
                className="qd-input-block"
                placeholder="e.g. Matte Black, Pearl White"
                value={form.color}
                onChange={(e) => update("color", e.target.value)}
              />
            </FieldRow>

            <WizardFooter>
              <button data-testid="buy-continue-intent" className="btn-primary w-full" onClick={next}>Continue</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 6 — Almost Done summary (compact) */}
        {step === 6 && (
          <section data-testid="buy-step-summary">
            <h2 className="qd-h1 text-2xl text-white mb-2">Quick Check</h2>
            <p className="text-white/55 text-sm mb-6">Confirm your requirement before sharing contact details.</p>

            <div className="qd-card p-4 mb-5 space-y-2 text-[13px]">
              <SummaryRow label="Budget" value={budgetSummary(form)} />
              <SummaryRow label="Brand" value={listSummary(form.brands)} />
              <SummaryRow label="Vehicle Type" value={listSummary(form.body_types)} />
              <SummaryRow label="Year" value={form.year_min ? `${form.year_min}+` : "No preference"} />
              <SummaryRow label="Max KM" value={form.max_km ? `Under ${form.max_km.toLocaleString()} km` : "No preference"} />
              <SummaryRow label="Fuel" value={listSummary(form.fuel_types)} />
              <SummaryRow label="Transmission" value={listSummary(form.transmissions)} />
              <SummaryRow label="Ownership" value={(OWNERSHIP_OPTIONS.find((o) => o.v === form.ownership) || {}).l} />
              {form.preferred_model && <SummaryRow label="Preferred Model" value={form.preferred_model} />}
              <SummaryRow label="Timeline" value={(INTENT_OPTIONS.find((i) => i.v === form.intent) || {}).l} />
              <SummaryRow label="Finance" value={form.finance_required ? "Yes" : "No"} />
              <SummaryRow label="Exchange" value={form.exchange ? "Yes" : "No"} />
              {form.color && <SummaryRow label="Colour" value={form.color} />}
            </div>

            <WizardFooter>
              <button data-testid="buy-continue-summary" className="btn-primary w-full" onClick={next}>Looks Good — Share Contact</button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 7 — Contact */}
        {step === 7 && (
          <section data-testid="buy-step-contact">
            <h2 className="qd-h1 text-2xl text-white mb-2">How can we reach you?</h2>
            <p className="text-white/55 text-sm mb-6">Our sourcing specialist will contact you directly. No OTP, no spam.</p>

            <FieldRow label="Full Name">
              <input
                data-testid="buy-name" autoComplete="name"
                className="qd-input-block"
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
                placeholder="Your full name"
              />
            </FieldRow>
            <FieldRow label="Mobile Number">
              <div className="flex items-center gap-3">
                <span className="text-white/55 text-sm pb-3 border-b-[1.5px] border-white/22">+91</span>
                <input
                  data-testid="buy-phone"
                  className="qd-input flex-1"
                  inputMode="numeric" autoComplete="tel-national" maxLength={10}
                  value={form.contact_phone}
                  onChange={(e) => {
                    let d = e.target.value.replace(/\D/g, "");
                    if (d.length > 10 && d.startsWith("91")) d = d.slice(2);
                    if (d.length > 10 && d.startsWith("0")) d = d.slice(1);
                    update("contact_phone", d.slice(0, 10));
                  }}
                  placeholder="98765 43210"
                />
              </div>
            </FieldRow>
            <FieldRow label="Prefer WhatsApp?">
              <ChipSingle testid="wapp" value={form.whatsapp_preferred ? "yes" : "no"} options={YES_NO}
                onPick={(v) => update("whatsapp_preferred", v === "yes")} />
            </FieldRow>
            <FieldRow label="Best Time to Contact">
              <ChipSingle testid="time" value={form.best_time} options={BEST_TIME_CHIPS}
                onPick={(v) => update("best_time", v)} />
            </FieldRow>

            <WizardFooter>
              <button
                data-testid="buy-submit-btn"
                className="btn-primary w-full"
                onClick={submit}
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit Requirement"}
              </button>
            </WizardFooter>
          </section>
        )}

        {/* STEP 8 — Success */}
        {step === INPUT_STEPS + 1 && (
          <section className="text-center pt-8" data-testid="buy-step-success">
            <CheckCircle size={64} weight="fill" className="text-[#FF1E1E] mx-auto mb-6" />
            <h2 className="qd-h1 text-3xl text-white mb-3">Requirement Submitted</h2>
            <p className="text-white/60 mb-10 max-w-sm mx-auto">
              A Q Drives sourcing specialist will contact you on <span className="text-white">+91 {form.contact_phone}</span> shortly.
            </p>
            <div className="flex flex-col gap-3 max-w-sm mx-auto">
              <a
                data-testid="buy-wa-btn"
                href={waLink("Hi Q Drives, I just submitted a buyer requirement on the app.")}
                target="_blank" rel="noreferrer"
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <WhatsappLogo size={16} weight="fill" /> WhatsApp Q Drives
              </a>
              <button data-testid="buy-return-home" className="btn-secondary w-full" onClick={() => nav("/")}>Return Home</button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ---------- Helpers ----------
function FieldRow({ label, children }) {
  return <div className="mb-5"><label className="qd-label">{label}</label>{children}</div>;
}

function ChipMulti({ value, options, onToggle, onClear, testid }) {
  const items = options.map((o) => (typeof o === "string" ? { v: o, l: o } : o));
  return (
    <div className="flex flex-wrap gap-2">
      <button
        data-testid={`${testid}-no-preference`}
        className={`qd-chip ${value.length === 0 ? "active" : ""}`}
        onClick={onClear}
      >No Preference</button>
      {items.map((o) => (
        <button key={o.v} data-testid={`${testid}-${o.v}`}
          className={`qd-chip ${value.includes(o.v) ? "active" : ""}`}
          onClick={() => onToggle(o.v)}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function ChipSingle({ value, options, onPick, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={String(o.v)} data-testid={`${testid}-${o.v}`}
          className={`qd-chip ${value === o.v ? "active" : ""}`}
          onClick={() => onPick(o.v)}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function ChipGrid({ value, options, onPick, testid, cols = 2 }) {
  // Card-style chip grid for prominent selectors (Year, KM, Budget).
  const gridCls = cols === 3 ? "grid-cols-3" : "grid-cols-2";
  return (
    <div className={`grid ${gridCls} gap-2.5`}>
      {options.map((o) => (
        <button key={String(o.v)} data-testid={`${testid}-${o.v ?? "any"}`}
          className={`qd-chip-card ${value === o.v ? "active" : ""}`}
          onClick={() => onPick(o.v)}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-white/5 last:border-0 py-1.5">
      <span className="text-white/55">{label}</span>
      <span className="text-white text-right">{value || "—"}</span>
    </div>
  );
}

function budgetSummary(f) {
  if (f.budget_bucket === "custom") {
    if (!f.budget_min && !f.budget_max) return "—";
    return `₹${(f.budget_min / 100000).toFixed(1)}L – ₹${(f.budget_max / 100000).toFixed(1)}L`;
  }
  const b = BUDGET_BUCKETS.find((x) => x.v === f.budget_bucket);
  return b ? b.l : "—";
}
function listSummary(arr) {
  if (!arr || arr.length === 0) return "No preference";
  if (arr.length <= 3) return arr.join(", ");
  return `${arr.slice(0, 2).join(", ")} +${arr.length - 2} more`;
}
